import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import api from '../utils/api';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';

const TABS = ['farmProfile', 'myProducts', 'products', 'labourRates', 'bankAccounts', 'fields', 'seasons', 'users'];

const CITIES = [
  'Adana','Adıyaman','Afyonkarahisar','Ağrı','Aksaray','Amasya','Ankara','Antalya','Ardahan','Artvin',
  'Aydın','Balıkesir','Bartın','Batman','Bayburt','Bilecik','Bingöl','Bitlis','Bolu','Burdur',
  'Bursa','Çanakkale','Çankırı','Çorum','Denizli','Diyarbakır','Düzce','Edirne','Elazığ','Erzincan',
  'Erzurum','Eskişehir','Gaziantep','Giresun','Gümüşhane','Hakkari','Hatay','Iğdır','Isparta','İstanbul',
  'İzmir','Kahramanmaraş','Karabük','Karaman','Kars','Kastamonu','Kayseri','Kırıkkale','Kırklareli','Kırşehir',
  'Kilis','Kocaeli','Konya','Kütahya','Malatya','Manisa','Mardin','Mersin','Muğla','Muş',
  'Nevşehir','Niğde','Ordu','Osmaniye','Rize','Sakarya','Samsun','Şanlıurfa','Siirt','Sinop',
  'Şırnak','Sivas','Tekirdağ','Tokat','Trabzon','Tunceli','Uşak','Van','Yalova','Yozgat','Zonguldak'
];

const PRODUCT_CATEGORIES = [
  { value: 'crop', label: 'Ürün' },
  { value: 'livestock', label: 'Hayvan' },
  { value: 'seed', label: 'Tohum' },
  { value: 'fertilizer', label: 'Gübre' },
  { value: 'pesticide', label: 'İlaç' },
  { value: 'fuel', label: 'Yakıt' },
  { value: 'material', label: 'Malzeme' },
  { value: 'other', label: 'Diğer' },
];

const UNITS = ['kg', 'ton', 'litre', 'adet', 'çuval', 'kasa', 'dekar', 'deste'];
const LABOUR_UNITS = ['gün', 'saat', 'dekar'];
const FARM_TYPES = [
  { value: 'crop', label: 'Bitkisel Üretim' },
  { value: 'livestock', label: 'Hayvancılık' },
  { value: 'mixed', label: 'Karışık' },
  { value: 'orchard', label: 'Bahçecilik' },
];

// ─── TAB 1: Farm Profile ──────────────────────────────────────────────────────

function FarmProfileTab({ t }) {
  const [farm, setFarm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/settings/farm').then(r => setFarm(r.data.data)).catch(console.error);
  }, []);

  const set = (k, v) => setFarm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/farm', {
        name: farm.name, ownerName: farm.owner_name, phone: farm.phone,
        email: farm.email, address: farm.address, city: farm.city,
        district: farm.district, isCompany: farm.is_company, taxNumber: farm.tax_number,
        farmType: farm.farm_type, totalLandDecare: farm.total_land_decare,
      });
      toast.success(t('settings.saved'));
    } catch { toast.error(t('settings.saveFailed')); }
    finally { setSaving(false); }
  };

  if (!farm) return <div className="empty-state"><p>{t('common.loading')}</p></div>;

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="form-group">
        <label>{t('settings.farm.name')} *</label>
        <input value={farm.name || ''} onChange={e => set('name', e.target.value)} required />
      </div>
      <div className="form-group">
        <label>{t('settings.farm.ownerName')} *</label>
        <input value={farm.owner_name || ''} onChange={e => set('owner_name', e.target.value)} required />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>{t('settings.farm.phone')}</label>
          <input type="tel" value={farm.phone || ''} onChange={e => set('phone', e.target.value)} />
        </div>
        <div className="form-group">
          <label>{t('settings.farm.email')}</label>
          <input type="email" value={farm.email || ''} onChange={e => set('email', e.target.value)} />
        </div>
      </div>
      <div className="form-group">
        <label>{t('settings.farm.address')}</label>
        <textarea value={farm.address || ''} onChange={e => set('address', e.target.value)} rows={2} />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>{t('settings.farm.city')}</label>
          <select value={farm.city || ''} onChange={e => set('city', e.target.value)}>
            <option value="">--</option>
            {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>{t('settings.farm.district')}</label>
          <input value={farm.district || ''} onChange={e => set('district', e.target.value)} />
        </div>
      </div>
      <div className="form-group">
        <label>{t('settings.farm.farmType')}</label>
        <select value={farm.farm_type || ''} onChange={e => set('farm_type', e.target.value)}>
          <option value="">--</option>
          {FARM_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>{t('settings.farm.totalLand')}</label>
        <input type="number" value={farm.total_land_decare || ''} onChange={e => set('total_land_decare', e.target.value)} placeholder="dekar" />
      </div>
      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={farm.is_company || false} onChange={e => set('is_company', e.target.checked)} />
          {t('settings.farm.isCompany')}
        </label>
      </div>
      {farm.is_company && (
        <div className="form-group">
          <label>{t('settings.farm.taxNumber')}</label>
          <input value={farm.tax_number || ''} onChange={e => set('tax_number', e.target.value)} />
        </div>
      )}
      <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
        onClick={handleSave} disabled={saving}>
        {saving ? t('common.loading') : t('common.save')}
      </button>
    </div>
  );
}

// ─── TAB: My Products (Ürünlerim) ────────────────────────────────────────────

const PRODUCT_TYPE_CHIPS = [
  { value: '', label: 'Tümü' },
  { value: 'Meyve', label: 'Meyve' },
  { value: 'Sebze', label: 'Sebze' },
  { value: 'Tahıl', label: 'Tahıl' },
  { value: 'Bakliyat', label: 'Bakliyat' },
  { value: 'Hayvansal', label: 'Hayvansal' },
  { value: 'Tohum', label: 'Tohum' },
  { value: 'Gübre', label: 'Gübre' },
  { value: 'İlaç', label: 'İlaç' },
  { value: 'Yakıt', label: 'Yakıt' },
  { value: 'Malzeme', label: 'Malzeme' },
];

function MyProductsTab({ t }) {
  const [farmProducts, setFarmProducts] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showCatalog, setShowCatalog] = useState(false);
  const [importRows, setImportRows] = useState(null); // null = closed, [] = preview open
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const onImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (res) => {
        const rows = (res.data || []).filter(r => (r.name || '').trim());
        if (rows.length === 0) { toast.error('Dosyada ürün bulunamadı'); return; }
        setImportRows(rows);
      },
      error: () => toast.error('Dosya okunamadı'),
    });
    e.target.value = '';
  };

  const doImport = async () => {
    setImporting(true);
    try {
      const r = await api.post('/master/products/import', { products: importRows });
      toast.success(`${r.data.total} ürün kataloğa eklendi/güncellendi`);
      setImportRows(null);
      loadCatalog();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || 'İçe aktarılamadı');
    } finally { setImporting(false); }
  };

  const loadFarm = useCallback(() => {
    api.get('/farm-products?includeStock=true').then(r => setFarmProducts(r.data.data || [])).catch(console.error);
  }, []);

  const loadCatalog = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    params.set('autoFilter', 'true'); // show products relevant to this farm's type first
    api.get(`/farm-products/catalog?${params}`)
      .then(r => setCatalog(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { loadFarm(); loadCatalog(); }, [loadFarm, loadCatalog]);

  const addProduct = async (mpId) => {
    try {
      await api.post('/farm-products', { masterProductId: mpId });
      toast.success(t('settings.saved'));
      loadFarm(); loadCatalog();
    } catch { toast.error(t('settings.saveFailed')); }
  };

  const removeProduct = async (mpId) => {
    try {
      const r = await api.delete(`/farm-products/${mpId}`);
      if (r.data.canDelete === false) { toast.error(r.data.reason); return; }
      toast.success(t('settings.deleted'));
      loadFarm(); loadCatalog();
    } catch { toast.error(t('settings.deleteFailed')); }
  };

  const toggleFav = async (mpId, current) => {
    try {
      await api.put(`/farm-products/${mpId}/favourite`, { isFavourite: !current });
      loadFarm();
    } catch { toast.error(t('settings.saveFailed')); }
  };

  const favourites = (typeFilter ? farmProducts.filter(p => p.product_type === typeFilter) : farmProducts).filter(p => p.is_favourite);
  const nonFavs = (typeFilter ? farmProducts.filter(p => p.product_type === typeFilter) : farmProducts).filter(p => !p.is_favourite);
  const filteredCatalog = typeFilter
    ? catalog.filter(p => p.product_type === typeFilter && !p.is_selected)
    : catalog.filter(p => !p.is_selected);

  return (
    <div>
      <div className="chip-group" style={{ marginBottom: 8 }}>
        {PRODUCT_TYPE_CHIPS.map(f => (
          <button key={f.value} className={`chip${typeFilter === f.value ? ' active' : ''}`}
            onClick={() => setTypeFilter(f.value)}>{f.label}</button>
        ))}
      </div>

      {/* Favourites section */}
      <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 4, color: '#e67e22' }}>
        ⭐ {t('favourites.favouriteProducts')} ({favourites.length})
      </div>
      <div style={{ fontSize: '0.72rem', color: '#aaa', marginBottom: 8 }}>{t('favourites.infoText')}</div>
      <div className="card" style={{ marginBottom: 12 }}>
        {favourites.length === 0 ? (
          <div className="empty-state" style={{ padding: 16 }}><p style={{ fontSize: '0.82rem' }}>{t('favourites.noFavourites')}</p></div>
        ) : favourites.map(item => (
          <div key={item.farm_product_id || item.master_product_id} className="txn-item">
            <button onClick={() => toggleFav(item.master_product_id, true)}
              style={{ background: 'none', border: 'none', fontSize: '1rem', cursor: 'pointer', padding: 0, marginRight: 6 }}>⭐</button>
            <div className="txn-main" style={{ flex: 1 }}>
              <div className="txn-name">{item.name}</div>
              <div className="txn-meta">
                {item.product_type || item.category} · {item.unit}
                {item.stock_quantity > 0 && ` · Stok: ${new Intl.NumberFormat('tr-TR').format(item.stock_quantity)} ${item.stock_display_unit || item.unit}`}
                {item.usage_count > 0 && <span style={{ marginLeft: 4, color: '#bbb' }}>{item.usage_count} {t('favourites.usedCount')}</span>}
              </div>
            </div>
            <button onClick={() => removeProduct(item.master_product_id)}
              style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontSize: '0.9rem' }}>✕</button>
          </div>
        ))}
      </div>

      {/* Non-favourites section */}
      <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 4, color: 'var(--green-dark)' }}>
        {t('favourites.otherProducts')} ({nonFavs.length})
      </div>
      <div className="card" style={{ marginBottom: 16 }}>
        {nonFavs.length === 0 ? (
          <div className="empty-state" style={{ padding: 16 }}><p style={{ fontSize: '0.82rem' }}>{t('farmProducts.noProducts')}</p></div>
        ) : nonFavs.map(item => (
          <div key={item.farm_product_id || item.master_product_id} className="txn-item">
            <button onClick={() => toggleFav(item.master_product_id, false)}
              style={{ background: 'none', border: 'none', fontSize: '1rem', cursor: 'pointer', padding: 0, marginRight: 6 }}>☆</button>
            <div className="txn-main" style={{ flex: 1 }}>
              <div className="txn-name">{item.name}</div>
              <div className="txn-meta">
                {item.product_type || item.category} · {item.unit}
                {item.stock_quantity > 0 && ` · Stok: ${new Intl.NumberFormat('tr-TR').format(item.stock_quantity)} ${item.stock_display_unit || item.unit}`}
              </div>
            </div>
            <button onClick={() => removeProduct(item.master_product_id)}
              style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontSize: '0.9rem' }}>✕</button>
          </div>
        ))}
      </div>

      {/* Catalog section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--green-dark)' }}>
          {t('farmProducts.addFromCatalog')}
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={onImportFile} />
          <button onClick={() => fileRef.current && fileRef.current.click()}
            style={{ background: '#eef6ff', border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', color: '#2c6fbf' }}>
            📄 Excel'den Yükle
          </button>
          <button onClick={() => setShowCatalog(!showCatalog)}
            style={{ background: 'var(--green-pale)', border: 'none', borderRadius: 8,
              padding: '4px 10px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', color: 'var(--green-mid)' }}>
            {showCatalog ? t('common.close') : t('common.add')}
          </button>
        </div>
      </div>
      <div style={{ fontSize: '0.7rem', color: '#aaa', marginBottom: 8 }}>
        CSV şablonu: <a href="/urun-import-sablonu.csv" download style={{ color: '#2c6fbf' }}>urun-import-sablonu.csv</a>
      </div>

      {showCatalog && (
        <>
          <div className="form-group" style={{ marginBottom: 8 }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('common.search')} style={{ fontSize: '0.85rem' }} />
          </div>
          <div className="card">
            {loading ? <div className="empty-state"><p>{t('common.loading')}</p></div>
              : filteredCatalog.length === 0 ? <div className="empty-state"><p>{t('common.noData')}</p></div>
              : Object.entries(filteredCatalog.reduce((acc, p) => {
                  const k = p.subcategory || p.product_type || 'Diğer';
                  (acc[k] = acc[k] || []).push(p); return acc;
                }, {})).map(([sub, rows]) => (
                  <div key={sub}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7a9472', padding: '6px 2px 2px' }}>{sub}</div>
                    {rows.map(item => (
                      <div key={item.id} className="txn-item">
                        <div className="txn-main">
                          <div className="txn-name">{item.name}</div>
                          <div className="txn-meta">
                            {item.product_type || item.category} · {item.unit}
                            {item.variety && ` · ${item.variety}`}
                          </div>
                        </div>
                        <button onClick={() => addProduct(item.id)}
                          style={{ background: 'var(--green-mid)', color: '#fff', border: 'none', borderRadius: 8,
                            padding: '3px 10px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>
                          ➕
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
          </div>
        </>
      )}

      {/* Excel/CSV import preview */}
      {importRows !== null && (
        <Modal title="Ürünleri İçe Aktar" onClose={() => setImportRows(null)}>
          <p style={{ fontSize: '0.85rem', marginBottom: 10 }}>
            <b>{importRows.length}</b> ürün bulundu. Kataloğa eklensin mi?
          </p>
          <div className="card" style={{ maxHeight: 260, overflowY: 'auto', marginBottom: 12 }}>
            {importRows.slice(0, 50).map((r, i) => (
              <div key={i} style={{ padding: '5px 0', borderBottom: '1px solid #f2f2f2', fontSize: '0.8rem' }}>
                <b>{r.name}</b>
                <span style={{ color: '#888' }}> · {[r.category, r.subcategory, r.unit].filter(Boolean).join(' · ')}</span>
              </div>
            ))}
            {importRows.length > 50 && <div style={{ padding: '5px 0', fontSize: '0.75rem', color: '#aaa' }}>+{importRows.length - 50} ürün daha…</div>}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setImportRows(null)}>İptal</button>
            <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={importing} onClick={doImport}>
              {importing ? 'Aktarılıyor...' : `✅ ${importRows.length} ürünü ekle`}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── TAB 2: Products ──────────────────────────────────────────────────────────

function ProductsTab({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [catFilter, setCatFilter] = useState('');
  const [form, setForm] = useState({ name: '', category: 'crop', unit: 'kg', description: '' });

  const load = useCallback(() => {
    setLoading(true);
    api.get('/settings/products').then(r => setItems(r.data.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/settings/products', form);
      toast.success(t('settings.saved'));
      setShowModal(false);
      setForm({ name: '', category: 'crop', unit: 'kg', description: '' });
      load();
    } catch { toast.error(t('settings.saveFailed')); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/settings/products/${id}`);
      toast.success(t('settings.deleted'));
      load();
    } catch { toast.error(t('settings.deleteFailed')); }
  };

  const filtered = catFilter ? items.filter(i => i.category === catFilter) : items;

  return (
    <div>
      <div className="chip-group">
        <button className={`chip${catFilter === '' ? ' active' : ''}`} onClick={() => setCatFilter('')}>{t('settings.products.all')}</button>
        {PRODUCT_CATEGORIES.map(c => (
          <button key={c.value} className={`chip${catFilter === c.value ? ' active' : ''}`}
            onClick={() => setCatFilter(c.value)}>{c.label}</button>
        ))}
      </div>
      <div className="card">
        {loading ? <div className="empty-state"><p>{t('common.loading')}</p></div>
          : filtered.length === 0 ? <div className="empty-state"><div className="icon">📦</div><p>{t('common.noData')}</p></div>
          : filtered.map(item => (
            <div key={item.id} className="txn-item">
              <div className="txn-main">
                <div className="txn-name">{item.name}</div>
                <div className="txn-meta">
                  {PRODUCT_CATEGORIES.find(c => c.value === item.category)?.label} · {item.unit}
                  {item.description && ` · ${item.description}`}
                </div>
                {item.created_by_farm_name && (
                  <div style={{ fontSize: '0.7rem', color: '#bbb', marginTop: 2 }}>
                    {t('master.addedBy')}: {item.created_by_farm_name}
                  </div>
                )}
              </div>
              <button onClick={() => handleDelete(item.id)}
                style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontSize: '1rem' }}>
                {t('common.delete')}
              </button>
            </div>
          ))}
      </div>

      <button className="btn-fab" onClick={() => setShowModal(true)}>+</button>

      {showModal && (
        <Modal title={t('settings.products.add')} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>{t('settings.products.name')} *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('settings.products.category')} *</label>
                <select value={form.category} onChange={e => set('category', e.target.value)}>
                  {PRODUCT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>{t('settings.products.unit')}</label>
                <select value={form.unit} onChange={e => set('unit', e.target.value)}>
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>{t('settings.products.description')}</label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }}
                onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }}>
                {t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ─── TAB 3: Labour Rate Templates ─────────────────────────────────────────────

function LabourRatesTab({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ workType: '', defaultDailyRate: '', unit: 'gün', notes: '' });

  const load = useCallback(() => {
    setLoading(true);
    api.get('/settings/labour-rates').then(r => setItems(r.data.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/settings/labour-rates', form);
      toast.success(t('settings.saved'));
      setShowModal(false);
      setForm({ workType: '', defaultDailyRate: '', unit: 'gün', notes: '' });
      load();
    } catch { toast.error(t('settings.saveFailed')); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/settings/labour-rates/${id}`);
      toast.success(t('settings.deleted'));
      load();
    } catch { toast.error(t('settings.deleteFailed')); }
  };

  const formatTL = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(v);

  return (
    <div>
      <div className="card">
        {loading ? <div className="empty-state"><p>{t('common.loading')}</p></div>
          : items.length === 0 ? <div className="empty-state"><div className="icon">👷</div><p>{t('common.noData')}</p></div>
          : items.map(item => (
            <div key={item.id} className="txn-item">
              <div className="txn-main">
                <div className="txn-name">{item.work_type}</div>
                <div className="txn-meta">
                  {formatTL(item.default_daily_rate)} / {item.unit}
                  {item.notes && ` · ${item.notes}`}
                </div>
              </div>
              <button onClick={() => handleDelete(item.id)}
                style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontSize: '1rem' }}>
                {t('common.delete')}
              </button>
            </div>
          ))}
      </div>

      <button className="btn-fab" onClick={() => setShowModal(true)}>+</button>

      {showModal && (
        <Modal title={t('settings.labourRates.add')} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>{t('settings.labourRates.workType')} *</label>
              <input value={form.workType} onChange={e => set('workType', e.target.value)}
                placeholder="Hasat, Budama, Sulama..." required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('settings.labourRates.dailyRate')}</label>
                <input type="number" value={form.defaultDailyRate} onChange={e => set('defaultDailyRate', e.target.value)} />
              </div>
              <div className="form-group">
                <label>{t('settings.labourRates.unit')}</label>
                <select value={form.unit} onChange={e => set('unit', e.target.value)}>
                  {LABOUR_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>{t('settings.labourRates.notes')}</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }}
                onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }}>
                {t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ─── TAB 4: Bank Accounts ─────────────────────────────────────────────────────

function BankAccountsTab({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'bank', balance: '', iban: '' });

  const load = useCallback(() => {
    setLoading(true);
    api.get('/settings/bank-accounts').then(r => setItems(r.data.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/settings/bank-accounts', form);
      toast.success(t('settings.saved'));
      setShowModal(false);
      setForm({ name: '', type: 'bank', balance: '', iban: '' });
      load();
    } catch { toast.error(t('settings.saveFailed')); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/settings/bank-accounts/${id}`);
      toast.success(t('settings.deleted'));
      load();
    } catch { toast.error(t('settings.deleteFailed')); }
  };

  const formatTL = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(v);

  return (
    <div>
      <div className="card">
        {loading ? <div className="empty-state"><p>{t('common.loading')}</p></div>
          : items.length === 0 ? <div className="empty-state"><div className="icon">🏦</div><p>{t('common.noData')}</p></div>
          : items.map(item => (
            <div key={item.id} className="txn-item">
              <div className="txn-main">
                <div className="txn-name">
                  {item.type === 'cash' ? '💵' : '🏦'} {item.name}
                </div>
                <div className="txn-meta">
                  {t('settings.bankAccounts.balance')}: {formatTL(item.balance)}
                  {item.iban && ` · ${item.iban}`}
                </div>
              </div>
              <button onClick={() => handleDelete(item.id)}
                style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontSize: '1rem' }}>
                {t('common.delete')}
              </button>
            </div>
          ))}
      </div>

      <button className="btn-fab" onClick={() => setShowModal(true)}>+</button>

      {showModal && (
        <Modal title={t('settings.bankAccounts.add')} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>{t('settings.bankAccounts.name')} *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="Ziraat Bankası, Nakit..." required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('settings.bankAccounts.type')}</label>
                <select value={form.type} onChange={e => set('type', e.target.value)}>
                  <option value="bank">{t('common.bank')}</option>
                  <option value="cash">{t('common.cash')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('settings.bankAccounts.balance')}</label>
                <input type="number" value={form.balance} onChange={e => set('balance', e.target.value)} placeholder="0.00" />
              </div>
            </div>
            {form.type === 'bank' && (
              <div className="form-group">
                <label>IBAN</label>
                <input value={form.iban} onChange={e => set('iban', e.target.value)} placeholder="TR..." />
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }}
                onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }}>
                {t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ─── TAB: Fields (Tarlalar) ──────────────────────────────────────────────────

const SOIL_TYPES_SETTINGS = ['Killi','Kumlu','Tınlı','Humuslu','Kireçli','Karma'];

function FieldsTab({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', sizeDecare: '', soilType: '', currentCrop: '', notes: '' });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/farm/fields').then(r => setItems(r.data.data || [])).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/farm/fields/${editingId}`, form);
      } else {
        await api.post('/farm/fields', form);
      }
      toast.success(t('settings.saved'));
      setShowModal(false);
      setForm({ name: '', sizeDecare: '', soilType: '', currentCrop: '', notes: '' });
      setEditingId(null);
      load();
    } catch { toast.error(t('settings.saveFailed')); }
  };

  const startEdit = (item) => {
    setForm({ name: item.name || '', sizeDecare: item.size_decare || '', soilType: item.soil_type || '', currentCrop: item.current_crop || '', notes: item.notes || '' });
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu tarlaya bağlı kayıtlar olabilir. Silmek istediğinizden emin misiniz?')) return;
    try {
      await api.delete(`/farm/fields/${id}`);
      toast.success(t('settings.deleted'));
      load();
    } catch { toast.error(t('settings.deleteFailed')); }
  };

  return (
    <div>
      <div className="card">
        {loading ? <div className="empty-state"><p>{t('common.loading')}</p></div>
          : items.length === 0 ? <div className="empty-state"><div className="icon">🗺️</div><p>{t('common.noData')}</p></div>
          : items.map(item => (
            <div key={item.id} className="txn-item">
              <div className="txn-main" onClick={() => startEdit(item)} style={{ cursor: 'pointer' }}>
                <div className="txn-name">🌿 {item.name}</div>
                <div className="txn-meta">
                  {item.size_decare && `${item.size_decare} da`}
                  {item.soil_type && ` · ${item.soil_type}`}
                  {item.current_crop && ` · 🌾 ${item.current_crop}`}
                </div>
              </div>
              <button onClick={() => handleDelete(item.id)}
                style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontSize: '0.9rem' }}>✕</button>
            </div>
          ))}
      </div>
      <button className="btn-fab" onClick={() => { setForm({ name: '', sizeDecare: '', soilType: '', currentCrop: '', notes: '' }); setEditingId(null); setShowModal(true); }}>+</button>
      {showModal && (
        <Modal title={editingId ? 'Tarla Düzenle' : 'Yeni Tarla Ekle'} onClose={() => { setShowModal(false); setEditingId(null); }}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Tarla Adı *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Kuzey Tarlası..." required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Büyüklük (dekar)</label>
                <input type="number" step="0.1" value={form.sizeDecare} onChange={e => set('sizeDecare', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Toprak Türü</label>
                <select value={form.soilType} onChange={e => set('soilType', e.target.value)}>
                  <option value="">Seçin</option>
                  {SOIL_TYPES_SETTINGS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Mevcut Ürün</label>
              <input value={form.currentCrop} onChange={e => set('currentCrop', e.target.value)} placeholder="Buğday, Domates..." />
            </div>
            <div className="form-group">
              <label>Notlar</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => { setShowModal(false); setEditingId(null); }}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }}>{t('common.save')}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ─── TAB: Seasons (Sezonlar) ─────────────────────────────────────────────────

const SEASON_TYPES = [
  { value: 'bahar', label: 'Bahar' },
  { value: 'yaz', label: 'Yaz' },
  { value: 'sonbahar', label: 'Sonbahar' },
  { value: 'kış', label: 'Kış' },
  { value: 'yıllık', label: 'Yıllık' },
];

function SeasonsTab({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', seasonType: 'bahar', startDate: '', endDate: '', notes: '' });

  const load = useCallback(() => {
    setLoading(true);
    api.get('/seasons').then(r => setItems(r.data.data || [])).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/seasons', form);
      toast.success(t('settings.saved'));
      setShowModal(false);
      setForm({ name: '', seasonType: 'bahar', startDate: '', endDate: '', notes: '' });
      load();
    } catch { toast.error(t('settings.saveFailed')); }
  };

  const activate = async (id) => {
    try {
      await api.put(`/seasons/${id}/activate`);
      toast.success(t('settings.saved'));
      load();
    } catch { toast.error(t('settings.saveFailed')); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/seasons/${id}`);
      toast.success(t('settings.deleted'));
      load();
    } catch (err) { toast.error(err.response?.data?.error || t('settings.deleteFailed')); }
  };

  return (
    <div>
      <div className="card">
        {loading ? <div className="empty-state"><p>{t('common.loading')}</p></div>
          : items.length === 0 ? <div className="empty-state"><div className="icon">📅</div><p>{t('common.noData')}</p></div>
          : items.map(item => (
            <div key={item.id} className="txn-item">
              <div className="txn-main">
                <div className="txn-name">{item.name}</div>
                <div className="txn-meta">
                  {SEASON_TYPES.find(s => s.value === item.season_type)?.label || item.season_type}
                  {item.start_date && ` · ${new Date(item.start_date).toLocaleDateString('tr-TR')}`}
                  {item.end_date && ` - ${new Date(item.end_date).toLocaleDateString('tr-TR')}`}
                </div>
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 700, marginTop: 4,
                  background: item.is_active ? '#27ae60' : '#ccc', color: '#fff'
                }}>
                  {item.is_active ? 'Aktif' : 'Pasif'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {!item.is_active && (
                  <button onClick={() => activate(item.id)}
                    style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
                    Aktif Yap
                  </button>
                )}
                <button onClick={() => handleDelete(item.id)}
                  style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontSize: '0.9rem' }}>✕</button>
              </div>
            </div>
          ))}
      </div>
      <button className="btn-fab" onClick={() => setShowModal(true)}>+</button>
      {showModal && (
        <Modal title="Yeni Sezon" onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Sezon Adı *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="2025 Bahar" required />
            </div>
            <div className="form-group">
              <label>Tür</label>
              <select value={form.seasonType} onChange={e => set('seasonType', e.target.value)}>
                {SEASON_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Başlangıç</label>
                <input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Bitiş</label>
                <input type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }}>{t('common.save')}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ─── TAB 5: Users ─────────────────────────────────────────────────────────────

function UsersTab({ t }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'worker', phone: '' });

  const load = useCallback(() => {
    setLoading(true);
    api.get('/settings/users').then(r => setItems(r.data.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/settings/users', form);
      toast.success(t('settings.saved'));
      setShowModal(false);
      setForm({ name: '', email: '', password: '', role: 'worker', phone: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || t('settings.saveFailed'));
    }
  };

  if (user?.role !== 'owner') {
    return <div className="card" style={{ padding: 16 }}>
      <div className="empty-state"><p>{t('settings.users.ownerOnly')}</p></div>
    </div>;
  }

  return (
    <div>
      <div className="card">
        {loading ? <div className="empty-state"><p>{t('common.loading')}</p></div>
          : items.length === 0 ? <div className="empty-state"><div className="icon">👤</div><p>{t('common.noData')}</p></div>
          : items.map(item => (
            <div key={item.id} className="txn-item">
              <div className="txn-main">
                <div className="txn-name">{item.name}</div>
                <div className="txn-meta">
                  {item.email} · {item.role}
                  {!item.is_active && <span style={{ color: '#c00', marginLeft: 6 }}>{t('settings.users.inactive')}</span>}
                </div>
              </div>
            </div>
          ))}
      </div>

      <button className="btn-fab" onClick={() => setShowModal(true)}>+</button>

      {showModal && (
        <Modal title={t('settings.users.add')} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>{t('settings.users.name')} *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} required />
            </div>
            <div className="form-group">
              <label>{t('settings.users.email')} *</label>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)} required />
            </div>
            <div className="form-group">
              <label>{t('settings.users.password')} *</label>
              <input type="password" value={form.password} onChange={e => set('password', e.target.value)}
                minLength={6} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('settings.users.role')}</label>
                <select value={form.role} onChange={e => set('role', e.target.value)}>
                  <option value="manager">{t('settings.users.roles.manager')}</option>
                  <option value="worker">{t('settings.users.roles.worker')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('settings.users.phone')}</label>
                <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }}
                onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }}>
                {t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ─── MAIN SETTINGS PAGE ───────────────────────────────────────────────────────

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState(0);
  const { t } = useTranslation();

  const tabLabels = [
    t('settings.tabs.farmProfile'),
    t('settings.tabs.myProducts'),
    t('settings.tabs.products'),
    t('settings.tabs.labourRates'),
    t('settings.tabs.bankAccounts'),
    t('settings.tabs.fields'),
    t('settings.tabs.seasons'),
    t('settings.tabs.users'),
  ];

  const renderTab = () => {
    switch (TABS[activeTab]) {
      case 'farmProfile':  return <FarmProfileTab t={t} />;
      case 'myProducts':   return <MyProductsTab t={t} />;
      case 'products':     return <ProductsTab t={t} />;
      case 'labourRates':  return <LabourRatesTab t={t} />;
      case 'bankAccounts': return <BankAccountsTab t={t} />;
      case 'fields':       return <FieldsTab t={t} />;
      case 'seasons':      return <SeasonsTab t={t} />;
      case 'users':        return <UsersTab t={t} />;
      default:             return null;
    }
  };

  return (
    <div>
      <h2 className="section-title">{t('nav.settings')}</h2>
      <div onClick={() => window.location.href = '/contacts'} className="card" style={{ padding: 12, marginBottom: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: '1.5rem' }}>👥</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Alıcı, Satıcı ve İşçiler</div>
          <div style={{ fontSize: '0.78rem', color: '#888' }}>Kişi ve işçi kayıtlarını yönetin</div>
        </div>
        <span style={{ color: '#aaa' }}>→</span>
      </div>
      <div className="settings-tabs">
        {tabLabels.map((label, i) => (
          <button key={i}
            className={`settings-tab${activeTab === i ? ' active' : ''}`}
            onClick={() => setActiveTab(i)}>
            {label}
          </button>
        ))}
      </div>
      {renderTab()}
    </div>
  );
}
